import { useCallback, useEffect, useState } from 'react'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { prototypeStoreRegistry } from '../../generated/prototypeRuntime'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { useMyFavouriteDishIds } from './projections'

/**
 * Dish favouriting, in one place so every surface behaves identically.
 *
 * For authenticated customers, the star is optimistic: the projection row is
 * written locally before the command resolves. The backend write is idempotent
 * in both directions. On failure we roll the local row back.
 *
 * For non-logged-in visitors, favourites are stored in local storage and
 * updated reactively. When the visitor later signs in, their guest favourites
 * are synced to their account profile.
 */
export interface FavouritesState {
  ids: ReadonlySet<string>
  isFavourite: (dishId: string) => boolean
  toggle: (dishId: string) => void
}

const GUEST_FAVOURITES_KEY = 'chow_guest_favourites'
const GUEST_EVENT = 'chow_guest_favourites_changed'

export const readGuestFavourites = (): ReadonlySet<string> => {
  if (typeof localStorage === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(GUEST_FAVOURITES_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed.map(String) : [])
  } catch {
    return new Set()
  }
}

export const writeGuestFavourites = (ids: ReadonlySet<string>): void => {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(GUEST_FAVOURITES_KEY, JSON.stringify(Array.from(ids)))
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(GUEST_EVENT))
    }
  } catch {
    /* storage quota exceeded or private mode */
  }
}

const favouriteStore = () =>
  prototypeStoreRegistry.get('menu.dish_favourites') as unknown as
    | TenantProjectionStore<Record<string, unknown>>
    | undefined

/** Local projection id for an optimistic star; stable per (profile, dish). */
const localFavouriteId = (profileId: string, dishId: string) => `local-fav-${profileId}-${dishId}`

/** Synchronize any guest favourites to the newly authenticated customer profile. */
export const syncGuestFavourites = async (profileId: string): Promise<void> => {
  if (!profileId || typeof localStorage === 'undefined') return
  const guest = readGuestFavourites()
  if (guest.size === 0) return

  // Clear guest storage so repeat visits do not re-sync
  localStorage.removeItem(GUEST_FAVOURITES_KEY)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(GUEST_EVENT))
  }

  const store = favouriteStore()
  for (const dishId of guest) {
    if (store) {
      store.upsert({
        id: localFavouriteId(profileId, dishId),
        profile_id: profileId,
        dish_id: dishId,
        created_at: new Date().toISOString(),
      })
    }
    if (isLiveRuntime()) {
      try {
        await commands.menu.favouriteDish(dishId, profileId)
      } catch {
        /* best-effort sync on auth */
      }
    }
  }
}

export const useFavourites = (profileId?: string): FavouritesState => {
  const authedIds = useMyFavouriteDishIds(profileId || '')
  const [guestIds, setGuestIds] = useState<ReadonlySet<string>>(() =>
    profileId ? new Set() : readGuestFavourites(),
  )

  useEffect(() => {
    if (profileId) {
      void syncGuestFavourites(profileId)
      return
    }

    const handler = () => {
      setGuestIds(readGuestFavourites())
    }
    if (typeof window !== 'undefined') {
      window.addEventListener(GUEST_EVENT, handler)
      window.addEventListener('storage', handler)
      return () => {
        window.removeEventListener(GUEST_EVENT, handler)
        window.removeEventListener('storage', handler)
      }
    }
  }, [profileId])

  const ids = profileId ? authedIds : guestIds

  const toggle = useCallback(
    (dishId: string) => {
      if (!dishId) return

      // Handle unauthenticated guest favourite
      if (!profileId) {
        const current = readGuestFavourites()
        const next = new Set(current)
        if (next.has(dishId)) {
          next.delete(dishId)
        } else {
          next.add(dishId)
        }
        writeGuestFavourites(next)
        setGuestIds(next)
        return
      }

      const store = favouriteStore()
      const starred = ids.has(dishId)
      const localId = localFavouriteId(profileId, dishId)

      // Optimistic local write first.
      if (store) {
        if (starred) {
          const existing = store
            .getSnapshot()
            .records.find(
              (r) =>
                String(r.profile_id ?? '') === profileId && String(r.dish_id ?? '') === dishId,
            )
          if (existing) store.remove?.(String(existing.id))
        } else {
          store.upsert({
            id: localId,
            profile_id: profileId,
            dish_id: dishId,
            created_at: new Date().toISOString(),
          })
        }
      }

      if (!isLiveRuntime()) return

      const command = starred
        ? commands.menu.unfavouriteDish(dishId, profileId)
        : commands.menu.favouriteDish(dishId, profileId)

      void command.catch(() => {
        // Roll the optimistic row back — the server is the authority.
        const rollbackStore = favouriteStore()
        if (!rollbackStore) return
        if (starred) {
          rollbackStore.upsert({
            id: localId,
            profile_id: profileId,
            dish_id: dishId,
            created_at: new Date().toISOString(),
          })
        } else {
          rollbackStore.remove?.(localId)
        }
      })
    },
    [ids, profileId],
  )

  return {
    ids,
    isFavourite: (dishId: string) => ids.has(dishId),
    toggle,
  }
}
